/**
 * Real persistence for the native survey builder (VER-109) — wires the builder's
 * SurveyPersistence contract to the /survey-templates HTTP client (client/api/survey.ts).
 *
 * This is the production replacement for `mockPersistence`. The builder speaks its own
 * SurveyDefinition tree (string id, `version`, status "draft"|"published"); the API speaks
 * PersistedSurveyTemplate (numeric id, `currentVersion`, status "Draft"|"Published", promoted
 * columns + an opaque `definition` blob). This module is the ONLY place that crosses that
 * boundary, so the two id/status/version representations never leak into the UI.
 *
 * See builder/INTEGRATION.md for the column-vs-blob contract.
 */
import type { SurveyDefinition } from "./survey.types";
import type { SurveyPersistence } from "./builder.persistence";
import {
    PersistedSurveyTemplate,
    SaveSurveyPayload,
    listSurveyTemplates,
    createSurveyTemplate,
    updateSurveyTemplate,
    publishSurveyTemplate,
    deleteSurveyTemplate,
} from "../../../client/api/survey";

/** Server ids are pure integers; every client-minted id (`survey-…`, `user-…`) has non-digits. */
const isServerId = (id: string): boolean => /^\d+$/.test(id);

/**
 * PersistedSurveyTemplate → SurveyDefinition. The nested tree (defaultScale/sections/scoring/
 * branchingRules/licence) is carried in the opaque `definition` blob; the promoted columns are
 * server-authoritative and OVERRIDE whatever the blob happens to hold (id/status/version drift).
 */
export const toDefinition = (t: PersistedSurveyTemplate): SurveyDefinition => {
    const tree = (t.definition ?? {}) as Partial<SurveyDefinition>;
    return {
        ...(tree as SurveyDefinition),
        id: String(t.id),
        code: t.code,
        title: t.title,
        instructions: t.instructions,
        scaleCode: t.scaleCode ?? undefined,
        higherIsBetter: t.higherIsBetter,
        status: t.status === "Published" ? "published" : "draft",
        version: t.currentVersion,
        isSystemSeed: t.isSystemSeed,
    };
};

/** SurveyDefinition → SaveSurveyPayload. The whole tree is stored as `definition`; `status` is
 *  intentionally omitted — the server only flips a template to Published via the publish endpoint. */
const toPayload = (def: SurveyDefinition): SaveSurveyPayload => ({
    code: def.code,
    title: def.title,
    instructions: def.instructions,
    scaleCode: def.scaleCode ?? null,
    higherIsBetter: def.higherIsBetter,
    definition: def,
});

export const apiPersistence: SurveyPersistence = {
    // Only editable, org-owned surveys belong in "Your surveys" — reference seeds render separately.
    onLoadSurveys: async () => (await listSurveyTemplates()).filter((t) => !t.isSystemSeed).map(toDefinition),

    onSaveSurvey: async (definition) => {
        const payload = toPayload(definition);
        const persisted = isServerId(definition.id)
            ? await updateSurveyTemplate(Number(definition.id), payload)
            : await createSurveyTemplate(payload);
        // Return the authoritative server copy — a freshly-created survey now carries its numeric id.
        return toDefinition(persisted);
    },

    onPublishSurvey: async (id) => toDefinition(await publishSurveyTemplate(Number(id))),

    onDeleteSurvey: async (id) => {
        await deleteSurveyTemplate(Number(id));
    },
};
